/**
 * KAN-962 — mouse selection inside a `CopyableValue` must not also "tap" the row around it.
 *
 * A drag-select that starts on an email and ends anywhere else produces ONE `click`, aimed at the
 * nearest common ancestor — usually the row — which would open the drawer under the user's hand.
 * `isCopyableDragClick(event)` is true only for that click: the mouse went DOWN inside a copyable
 * value, travelled more than a few pixels, and text inside a copyable value is now selected.
 *
 * It is never true for a plain click, even with an old selection still on screen (clicking a
 * `user-select: none` area does not clear a selection in Chrome), because the distance check is
 * against the mouse-down of THIS gesture.
 */

export const COPYABLE_ATTR = 'data-cvx-copyable';
const DRAG_PX = 4;

let lastDown = null;
let trackerInstalled = false;

const insideCopyable = (node) => {
  const el = node && node.nodeType === 1 ? node : node && node.parentElement;
  return Boolean(el && typeof el.closest === 'function' && el.closest(`[${COPYABLE_ATTR}]`));
};

/** Records where each mouse press began. Idempotent; installed by the first mounted CopyableValue. */
export function ensureMouseDownTracker() {
  if (trackerInstalled || typeof document === 'undefined') return;
  trackerInstalled = true;
  document.addEventListener(
    'mousedown',
    (e) => {
      lastDown = { x: e.clientX, y: e.clientY, inCopyable: insideCopyable(e.target) };
    },
    true,
  );
}

/** True while the page holds a non-empty text selection that touches a copyable value. */
export function selectionTouchesCopyable() {
  if (typeof window === 'undefined' || typeof window.getSelection !== 'function') return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  return insideCopyable(sel.anchorNode) || insideCopyable(sel.focusNode);
}

/** @param {{ clientX?: number, clientY?: number } | undefined} event the click event */
export function isCopyableDragClick(event) {
  if (!event || typeof event.clientX !== 'number' || !lastDown || !lastDown.inCopyable) return false;
  if (Math.hypot(event.clientX - lastDown.x, event.clientY - lastDown.y) <= DRAG_PX) return false;
  return selectionTouchesCopyable();
}

/** Test seam: forget the last mouse-down. */
export function __resetCopyableSelectionForTests() {
  lastDown = null;
}
